import React, { createRef, type SyntheticEvent, type MouseEvent, type KeyboardEvent } from 'react';
import { SkeletonItem } from '@atlaskit/side-navigation';
import { PROJECT_SETTINGS_PAGE_MODULE } from '@atlassian/jira-forge-ui-constants/src/constants.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { MenuItem } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/menu-item/main.tsx';
import { matchUrlPathname } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/url-matchers/index.tsx';
import type { ConnectDataItem } from '@atlassian/jira-navigation-apps-sidebar-connect/src/common/types.tsx';
import EcosystemMenu from '@atlassian/jira-navigation-apps-sidebar-ecosystem/src/ui/menu/index.tsx';
import type { ForgeItemProps } from '@atlassian/jira-navigation-apps-sidebar-forge/src/common/types.tsx';
import { SETTINGS_IDS } from '../../../constants.tsx';
import type { SettingFeatures } from '../../../types/index.tsx';
import { getSpaUrlForClassicSettings } from '../../../utils/servicedesk-spa-url-classic-settings/index.tsx';
import messages from './messages.tsx';

type IntegrationsMenuProps = {
	projectKey: string;
	features: SettingFeatures | null;
	onHandleLegacySPAClick: (
		e:
			| SyntheticEvent
			| MouseEvent<HTMLAnchorElement>
			| KeyboardEvent<HTMLAnchorElement>
			| MouseEvent<EventTarget>
			| KeyboardEvent<EventTarget>,
	) => void;
	ecosystemItems: {
		connect: ConnectDataItem[] | null;
		forge: ForgeItemProps[] | null;
	};
};

export const IntegrationsMenu = ({
	projectKey,
	features,
	onHandleLegacySPAClick,
	ecosystemItems,
}: IntegrationsMenuProps) => {
	const { formatMessage } = useIntl();
	const classicSettingsUrl = `/plugins/servlet/project-config/${projectKey}`;
	const EcoSystemOverrides = {
		BackMenuItem: {
			href: classicSettingsUrl,
			onClick: (ev: SyntheticEvent<Element, Event>) => ev.preventDefault(),
			title: formatMessage(messages.itemBack),
		},
	};
	if (features == null || projectKey == null) return <SkeletonItem />;

	return (
		<>
			<EcosystemMenu
				items={ecosystemItems}
				overrides={EcoSystemOverrides}
				forgeModule={PROJECT_SETTINGS_PAGE_MODULE}
				hasSeparator
				nestedMenuActive={false}
			/>
			{features.hasDevtools && (
				<MenuItem
					selectedOn={matchUrlPathname()}
					analytics={{ itemId: 'devtools' }}
					href={`/secure/ConfigureDevStatus.jspa?projectKey=${projectKey}`}
					ref={createRef()}
				>
					{formatMessage(messages.devtools)}
				</MenuItem>
			)}
			{features.hasJsdAppDiscovery && (
				<MenuItem
					analytics={{
						itemId: SETTINGS_IDS.JSD_APP_DISCOVERY,
					}}
					href={getSpaUrlForClassicSettings('apps', projectKey)}
					onClick={__SPA__ ? undefined : onHandleLegacySPAClick}
					selectedOn={matchUrlPathname()}
					ref={createRef()}
				>
					{formatMessage(messages.jsdAppDiscovery)}
				</MenuItem>
			)}
		</>
	);
};
